import React from "react";
import PropTypes from "prop-types";
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer,
} from "recharts";
import { humanEUR } from "../cost-control/forecast/format";

const ACTUAL_COLOR = "hsl(var(--chart-1))";
const PLAN_COLOR = "hsl(var(--chart-axis))";
const CAP_COLOR = "hsl(var(--danger))";
const GRID_COLOR = "hsl(var(--chart-grid))";
const AXIS_COLOR = "hsl(var(--chart-axis))";

// firstBreachDay — pure helper (BUD2): the first day whose forecast_cum reaches or
// exceeds cap, i.e. the projected date the budget would breach its limit. Returns
// null when cap is null/undefined, series is empty/missing, or the series never
// crosses cap (the budget is projected to stay under it all period).
export function firstBreachDay(series, cap) {
  if (cap == null || !Array.isArray(series) || series.length === 0) return null;
  const capN = Number(cap);
  for (const point of series) {
    if (Number(point.forecast_cum) >= capN) return point.day;
  }
  return null;
}

// BudgetBurnupChart — the cumulative burn-up vs limit for one budget (BUD2): a solid
// actual line (stops at "today" since actual_cum is null after — recharts breaks the
// line there for free), a dashed forecast continuation, a faint plan reference, a
// dashed danger cap line, and a breach-crossing marker where the forecast first
// reaches the cap.
export default function BudgetBurnupChart({ series = [], cap = null }) {
  const data = series.map((p) => ({
    day: p.day,
    actual_cum: p.actual_cum != null ? Number(p.actual_cum) : null,
    forecast_cum: p.forecast_cum != null ? Number(p.forecast_cum) : null,
    plan_cum: p.plan_cum != null ? Number(p.plan_cum) : null,
  }));
  const breachDay = firstBreachDay(series, cap);
  const hasCap = cap != null;

  return (
    <div style={{ width: "100%", height: 300 }}>
      <ResponsiveContainer>
        <ComposedChart data={data} margin={{ top: 12, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid stroke={GRID_COLOR} vertical={false} />
          <XAxis dataKey="day" stroke={AXIS_COLOR} tick={{ fontSize: 11 }} />
          <YAxis stroke={AXIS_COLOR} tick={{ fontSize: 11 }} tickFormatter={(v) => humanEUR(v)} />
          <Tooltip formatter={(v) => humanEUR(v)} />
          {hasCap && (
            <ReferenceLine
              y={Number(cap)}
              stroke={CAP_COLOR}
              strokeDasharray="4 4"
              label={{ value: `Limit ${humanEUR(cap)}`, position: "insideTopRight", fontSize: 10, fill: CAP_COLOR }}
            />
          )}
          {breachDay != null && (
            <ReferenceLine
              x={breachDay}
              stroke={CAP_COLOR}
              strokeDasharray="2 2"
              label={{ value: `breach ~${breachDay}`, position: "top", fontSize: 10, fill: CAP_COLOR }}
            />
          )}
          <Line type="monotone" dataKey="plan_cum" stroke={PLAN_COLOR} strokeWidth={1} dot={false} isAnimationActive={false} connectNulls />
          <Area type="monotone" dataKey="actual_cum" stroke={ACTUAL_COLOR} fill={ACTUAL_COLOR} fillOpacity={0.14} strokeWidth={2.5} dot={false} isAnimationActive={false} connectNulls={false} />
          <Line type="monotone" dataKey="forecast_cum" stroke={ACTUAL_COLOR} strokeWidth={2.5} strokeDasharray="6 4" dot={false} isAnimationActive={false} connectNulls={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

BudgetBurnupChart.propTypes = {
  series: PropTypes.arrayOf(PropTypes.shape({
    day: PropTypes.string,
    actual_cum: PropTypes.number, // may be null after today
    forecast_cum: PropTypes.number,
    plan_cum: PropTypes.number,
  })),
  cap: PropTypes.number, // may be null (Total view has no cap)
};
